/**
 * Rep Finder - Interactive Map Controller
 * 
 * Dependencies:
 * - SimpleMaps USA (simplemaps_usmap)
 * - SimpleMaps Canada (simplemaps_canadamap)
 * - window.repData (array of rep objects)
 * 
 * Usage:
 * 1. Include SimpleMaps JS files
 * 2. Define window.repData array
 * 3. Include this script
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────
  // State
  // ─────────────────────────────────────────
  const STATE = {
    activeZone: 'usa',
    activeRegion: null,
    previousRegion: null
  };

  // ─────────────────────────────────────────
  // Country Names — Comprehensive list (ISO 3166-1 alpha-2)
  // Any country not in this list will display its ISO code
  // ─────────────────────────────────────────
  const COUNTRY_NAMES = {
    // Americas
    US: 'United States', CA: 'Canada', MX: 'Mexico',
    BR: 'Brazil', AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru',
    VE: 'Venezuela', EC: 'Ecuador', BO: 'Bolivia', PY: 'Paraguay', UY: 'Uruguay',
    GY: 'Guyana', SR: 'Suriname', GF: 'French Guiana',
    PA: 'Panama', CR: 'Costa Rica', NI: 'Nicaragua', HN: 'Honduras', SV: 'El Salvador',
    GT: 'Guatemala', BZ: 'Belize',
    CU: 'Cuba', DO: 'Dominican Republic', HT: 'Haiti', JM: 'Jamaica', PR: 'Puerto Rico',
    TT: 'Trinidad and Tobago', BB: 'Barbados', BS: 'Bahamas', AW: 'Aruba',

    // Europe
    GB: 'United Kingdom', IE: 'Ireland', DE: 'Germany', FR: 'France', IT: 'Italy',
    ES: 'Spain', PT: 'Portugal', NL: 'Netherlands', BE: 'Belgium', LU: 'Luxembourg',
    AT: 'Austria', CH: 'Switzerland', LI: 'Liechtenstein',
    SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', IS: 'Iceland',
    PL: 'Poland', CZ: 'Czech Republic', SK: 'Slovakia', HU: 'Hungary',
    RO: 'Romania', BG: 'Bulgaria', HR: 'Croatia', SI: 'Slovenia', RS: 'Serbia',
    BA: 'Bosnia and Herzegovina', ME: 'Montenegro', MK: 'North Macedonia', AL: 'Albania', XK: 'Kosovo',
    GR: 'Greece', CY: 'Cyprus', MT: 'Malta',
    EE: 'Estonia', LV: 'Latvia', LT: 'Lithuania',
    UA: 'Ukraine', BY: 'Belarus', MD: 'Moldova',
    RU: 'Russia',

    // Middle East
    AE: 'United Arab Emirates', SA: 'Saudi Arabia', QA: 'Qatar', KW: 'Kuwait',
    BH: 'Bahrain', OM: 'Oman', YE: 'Yemen',
    IL: 'Israel', PS: 'Palestine', JO: 'Jordan', LB: 'Lebanon', SY: 'Syria',
    IQ: 'Iraq', IR: 'Iran', TR: 'Turkey',

    // Asia Pacific
    JP: 'Japan', CN: 'China', KR: 'South Korea', KP: 'North Korea',
    TW: 'Taiwan', HK: 'Hong Kong', MO: 'Macau',
    IN: 'India', PK: 'Pakistan', BD: 'Bangladesh', LK: 'Sri Lanka', NP: 'Nepal',
    BT: 'Bhutan', MV: 'Maldives', AF: 'Afghanistan',
    TH: 'Thailand', VN: 'Vietnam', MY: 'Malaysia', SG: 'Singapore', ID: 'Indonesia',
    PH: 'Philippines', MM: 'Myanmar', KH: 'Cambodia', LA: 'Laos', BN: 'Brunei',
    AU: 'Australia', NZ: 'New Zealand', FJ: 'Fiji', PG: 'Papua New Guinea',
    MN: 'Mongolia', KZ: 'Kazakhstan', UZ: 'Uzbekistan', TM: 'Turkmenistan',
    KG: 'Kyrgyzstan', TJ: 'Tajikistan',

    // Africa
    ZA: 'South Africa', EG: 'Egypt', MA: 'Morocco', DZ: 'Algeria', TN: 'Tunisia', LY: 'Libya',
    NG: 'Nigeria', GH: 'Ghana', KE: 'Kenya', ET: 'Ethiopia', TZ: 'Tanzania',
    UG: 'Uganda', RW: 'Rwanda', ZM: 'Zambia', ZW: 'Zimbabwe', BW: 'Botswana',
    NA: 'Namibia', MZ: 'Mozambique', AO: 'Angola', CD: 'DR Congo', CM: 'Cameroon',
    CI: 'Côte d\'Ivoire', SN: 'Senegal', ML: 'Mali', BF: 'Burkina Faso', NE: 'Niger',
    MG: 'Madagascar', MU: 'Mauritius', SC: 'Seychelles'
  };

  // ─────────────────────────────────────────
  // Regional Groupings for Int'l: Regions view
  // Countries not listed here will appear in "Other Countries"
  // ─────────────────────────────────────────
  const REGIONS = {
    'Americas': [
      'MX', 'BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY',
      'PA', 'CR', 'NI', 'HN', 'SV', 'GT', 'BZ',
      'CU', 'DO', 'HT', 'JM', 'PR', 'TT', 'BB', 'BS'
    ],
    'Europe': [
      'GB', 'IE', 'DE', 'FR', 'IT', 'ES', 'PT', 'NL', 'BE', 'LU',
      'AT', 'CH', 'LI', 'SE', 'NO', 'DK', 'FI', 'IS',
      'PL', 'CZ', 'SK', 'HU', 'RO', 'BG', 'HR', 'SI', 'RS', 'BA', 'ME', 'MK', 'AL',
      'GR', 'CY', 'MT', 'EE', 'LV', 'LT', 'UA', 'BY', 'MD', 'RU'
    ],
    'Middle East': [
      'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'YE',
      'IL', 'PS', 'JO', 'LB', 'SY', 'IQ', 'IR', 'TR'
    ],
    'Asia Pacific': [
      'JP', 'CN', 'KR', 'TW', 'HK', 'MO',
      'IN', 'PK', 'BD', 'LK', 'NP', 'MV',
      'TH', 'VN', 'MY', 'SG', 'ID', 'PH', 'MM', 'KH', 'LA', 'BN',
      'AU', 'NZ', 'FJ', 'PG', 'MN', 'KZ', 'UZ'
    ],
    'Africa': [
      'ZA', 'EG', 'MA', 'DZ', 'TN', 'LY',
      'NG', 'GH', 'KE', 'ET', 'TZ', 'UG', 'RW', 'ZM', 'ZW', 'BW',
      'NA', 'MZ', 'AO', 'CD', 'CM', 'CI', 'SN', 'MG', 'MU'
    ]
  };

  // Helper: Get all countries assigned to a region
  const ALL_REGION_COUNTRIES = new Set(Object.values(REGIONS).flat());

  // ─────────────────────────────────────────
  // Configuration
  // ─────────────────────────────────────────
  const ZONE_CONFIG = {
    usa: {
      mapObj: () => window.simplemaps_usmap,
      mapData: () => window.simplemaps_usmap_mapdata,
      containerId: 'usmap',
      zoneValue: 'USA',
      type: 'map'
    },
    canada: {
      mapObj: () => window.simplemaps_canadamap,
      mapData: () => window.simplemaps_canadamap_mapdata,
      containerId: 'canadamap',
      zoneValue: 'Canada',
      type: 'map'
    },
    'other-regions': {
      containerId: 'intl-regions',
      zoneValue: 'Other',
      type: 'regions'
    }
  };

  // Display order for rep types (hardcoded per spec)
  const TYPE_ORDER = [
    'Territory Manager',
    'House Account',
    'Sales Rep',
    'Showroom',
    'Authorized Dealer'
  ];

  // ─────────────────────────────────────────
  // DOM References
  // ─────────────────────────────────────────
  let $tabs, $maps, $regionTitle, $list, $empty, $placeholder, $dropdown, $srAnnouncements;

  function cacheDOMRefs() {
    $tabs = document.querySelectorAll('.rep-finder__tab');
    $maps = document.querySelectorAll('.rep-finder__map');
    $regionTitle = document.querySelector('.rep-finder__region-title');
    $list = document.querySelector('.rep-finder__list');
    $empty = document.querySelector('.rep-finder__empty');
    $placeholder = document.querySelector('.rep-finder__placeholder');
    $dropdown = document.getElementById('region-select');
    $srAnnouncements = document.getElementById('sr-announcements');
  }

  // ─────────────────────────────────────────
  // Screen Reader Announcements
  // ─────────────────────────────────────────
  function announceToScreenReader(message) {
    if ($srAnnouncements) {
      $srAnnouncements.textContent = message;
      // Clear after a delay to allow re-announcing the same message
      setTimeout(() => {
        $srAnnouncements.textContent = '';
      }, 1000);
    }
  }

  // ─────────────────────────────────────────
  // Helper: Parse regions (handles both array and comma-separated string)
  // ─────────────────────────────────────────
  function parseRegions(regions) {
    if (Array.isArray(regions)) {
      return regions.map(r => r.trim());
    }
    if (typeof regions === 'string') {
      return regions.split(',').map(r => r.trim()).filter(r => r);
    }
    return [];
  }

  // ─────────────────────────────────────────
  // Get International Countries from Data
  // ─────────────────────────────────────────
  function getInternationalCountries() {
    const countries = new Set();
    (window.repData || []).forEach(rep => {
      if (rep.zone === 'Other') {
        const regionList = parseRegions(rep.regions);
        regionList.forEach(r => countries.add(r));
      }
    });
    return Array.from(countries).sort((a, b) => {
      const nameA = COUNTRY_NAMES[a] || a;
      const nameB = COUNTRY_NAMES[b] || b;
      return nameA.localeCompare(nameB);
    });
  }

  // ─────────────────────────────────────────
  // Render Regional Groupings (Int'l: Regions)
  // ─────────────────────────────────────────
  function renderRegionalGroupings() {
    const container = document.getElementById('intl-regions');
    if (!container) return;

    const countries = new Set(getInternationalCountries());

    if (countries.size === 0) {
      container.innerHTML = '<p class="rep-finder__no-countries">No international representatives available.</p>';
      return;
    }

    let html = '<div class="rep-finder__regions">';

    Object.entries(REGIONS).forEach(([regionName, regionCountries]) => {
      // Filter to only countries that have reps
      const availableCountries = regionCountries.filter(c => countries.has(c));
      if (availableCountries.length === 0) return;

      html += `
        <div class="rep-finder__region-group">
          <h3 class="rep-finder__region-heading">${escapeHtml(regionName)}</h3>
          <div class="rep-finder__region-buttons">
      `;

      availableCountries.forEach(code => {
        const name = COUNTRY_NAMES[code] || code;
        html += `
          <button class="rep-finder__country-btn" data-region="${code}">
            <span class="rep-finder__country-name">${escapeHtml(name)}</span>
          </button>
        `;
      });

      html += `
          </div>
        </div>
      `;
    });

    // Add "Other Countries" for any countries not in a defined region
    const otherCountries = Array.from(countries)
      .filter(c => !ALL_REGION_COUNTRIES.has(c))
      .sort((a, b) => {
        const nameA = COUNTRY_NAMES[a] || a;
        const nameB = COUNTRY_NAMES[b] || b;
        return nameA.localeCompare(nameB);
      });

    if (otherCountries.length > 0) {
      html += `
        <div class="rep-finder__region-group">
          <h3 class="rep-finder__region-heading">Other Countries</h3>
          <div class="rep-finder__region-buttons">
      `;

      otherCountries.forEach(code => {
        const name = COUNTRY_NAMES[code] || code;
        html += `
          <button class="rep-finder__country-btn" data-region="${code}">
            <span class="rep-finder__country-name">${escapeHtml(name)}</span>
          </button>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    html += '</div>';
    container.innerHTML = html;

    // Add click handlers
    container.querySelectorAll('.rep-finder__country-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        // Remove active state from all buttons in this container
        container.querySelectorAll('.rep-finder__country-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        handleRegionClick(btn.dataset.region);
      });
    });
  }

  // ─────────────────────────────────────────
  // Populate Region Dropdown
  // ─────────────────────────────────────────
  function populateDropdown(zone) {
    if (!$dropdown) return;

    const config = ZONE_CONFIG[zone];
    if (!config) return;

    // Clear existing options
    $dropdown.innerHTML = '<option value="">Select a region...</option>';

    let regions = [];

    if (config.type === 'map' && config.mapData) {
      const mapData = config.mapData();
      if (mapData && mapData.state_specific) {
        // Get regions from map data
        regions = Object.entries(mapData.state_specific)
          .map(([code, data]) => ({
            code,
            name: data.name || code
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    // For world map, filter to only countries with reps (excluding US/CA)
    if (zone === 'other-map') {
      const countriesWithReps = new Set(getInternationalCountries());
      regions = regions.filter(r => countriesWithReps.has(r.code));
    }

    // Add options to dropdown
    regions.forEach(region => {
      const option = document.createElement('option');
      option.value = region.code;
      option.textContent = region.name;
      $dropdown.appendChild(option);
    });
  }


  // ─────────────────────────────────────────
  // Zone Switching
  // ─────────────────────────────────────────
  function switchZone(zone) {
    if (!ZONE_CONFIG[zone]) {
      console.warn(`Unknown zone: ${zone}`);
      return;
    }

    // Reset the current map before switching
    const currentConfig = ZONE_CONFIG[STATE.activeZone];
    const regionToReset = STATE.activeRegion;

    // Clear state BEFORE resetting map (so refresh_complete hook doesn't re-apply styling)
    STATE.activeRegion = null;
    STATE.previousRegion = null;

    if (currentConfig && currentConfig.type === 'map' && regionToReset) {
      const currentMapObj = currentConfig.mapObj ? currentConfig.mapObj() : null;
      if (currentMapObj) {
        resetStateToDefault(currentMapObj, regionToReset);
      }
    }

    STATE.activeZone = zone;

    // Update tab active states and ARIA attributes
    $tabs.forEach(tab => {
      const isActive = tab.dataset.zone === zone;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    // Show/hide map containers
    $maps.forEach(map => {
      const isActive = map.id === ZONE_CONFIG[zone].containerId;
      map.classList.toggle('is-active', isActive);
    });

    // Announce zone change to screen readers
    const zoneName = zone === 'usa' ? 'United States' : zone === 'canada' ? 'Canada' : 'International';
    announceToScreenReader(`Switched to ${zoneName} map`);

    // Clear results
    clearResults();

    // Populate dropdown for this zone
    populateDropdown(zone);

    const config = ZONE_CONFIG[zone];

    // Handle different zone types
    if (config.type === 'map') {
      // Refresh map when switching tabs
      const mapObj = config.mapObj ? config.mapObj() : null;
      if (mapObj && typeof mapObj.load === 'function') {
        if (mapObj.loaded === false) {
          mapObj.load();
        } else if (typeof mapObj.refresh === 'function') {
          // Refresh the map when switching tabs to fix sizing issues
          setTimeout(() => mapObj.refresh(), 50);
        }
      }
      // Show dropdown for map views
      if ($dropdown && $dropdown.parentElement) {
        $dropdown.parentElement.style.display = '';
      }
    } else if (config.type === 'regions') {
      renderRegionalGroupings();
      // Hide dropdown for regions view (has its own selection UI)
      if ($dropdown && $dropdown.parentElement) {
        $dropdown.parentElement.style.display = 'none';
      }
    }

    // Update URL
    updateURL();
  }

  // ─────────────────────────────────────────
  // Region Click Handler
  // ─────────────────────────────────────────
  function handleRegionClick(regionId) {
    // If on world map and clicking US or Canada, switch to that tab
    if (STATE.activeZone === 'other-map') {
      if (regionId === 'US') {
        switchZone('usa');
        return;
      }
      if (regionId === 'CA') {
        switchZone('canada');
        return;
      }
    }

    // Track previous region for resetting highlight
    STATE.previousRegion = STATE.activeRegion;
    STATE.activeRegion = regionId;

    // Sync dropdown value
    if ($dropdown) {
      $dropdown.value = regionId;
    }

    // Update map highlighting
    highlightSelectedRegion(regionId);

    const config = ZONE_CONFIG[STATE.activeZone];

    // Get region name
    let regionName = regionId;

    if (config.type === 'map' && config.mapData) {
      const mapData = config.mapData();
      if (mapData && mapData.state_specific && mapData.state_specific[regionId]) {
        regionName = mapData.state_specific[regionId].name || regionId;
      }
    } else {
      // For grid/regions, use our country names
      regionName = COUNTRY_NAMES[regionId] || regionId;
    }

    renderReps(regionId, regionName, config.zoneValue);
    updateURL();
  }

  // ─────────────────────────────────────────
  // Filter & Render Reps
  // ─────────────────────────────────────────
  function renderReps(regionId, regionName, zoneValue) {
    // Hide placeholder
    if ($placeholder) $placeholder.style.display = 'none';

    // Filter reps by zone and region
    const reps = (window.repData || []).filter(rep => {
      const matchesZone = rep.zone === zoneValue;
      const regionList = parseRegions(rep.regions);
      const matchesRegion = regionList.includes(regionId);
      return matchesZone && matchesRegion;
    });

    // Update title
    $regionTitle.textContent = `${regionName} Representatives`;

    // Handle empty state
    if (reps.length === 0) {
      $list.innerHTML = '';
      $empty.style.display = 'block';
      announceToScreenReader(`No representatives found for ${regionName}`);
      return;
    }

    $empty.style.display = 'none';

    // Announce results to screen readers
    announceToScreenReader(`Showing ${reps.length} representative${reps.length !== 1 ? 's' : ''} for ${regionName}`);

    // Group by type, then by company
    const grouped = groupReps(reps);
    $list.innerHTML = renderGroupedReps(grouped);
  }

  /**
   * Group reps by type, then by company
   * Returns: { type: { company: [reps] } }
   */
  function groupReps(reps) {
    const byType = {};

    reps.forEach(rep => {
      const type = rep.type || 'Sales Rep';
      const company = rep.company || 'Independent';

      if (!byType[type]) {
        byType[type] = {};
      }
      if (!byType[type][company]) {
        byType[type][company] = [];
      }
      byType[type][company].push(rep);
    });

    return byType;
  }

  /**
   * Render grouped reps as HTML
   */
  function renderGroupedReps(grouped) {
    let html = '';

    // Iterate in display order
    TYPE_ORDER.forEach(type => {
      if (!grouped[type]) return;

      html += `
        <div class="rep-finder__type-group">
          <h3 class="rep-finder__type-heading">${pluralize(type)}</h3>
      `;

      // Iterate companies within type
      Object.entries(grouped[type]).forEach(([company, reps]) => {
        html += `
          <div class="rep-finder__company-card">
            <h4 class="rep-finder__company-name">${escapeHtml(company)}</h4>
            <ul class="rep-finder__contacts">
        `;

        reps.forEach(rep => {
          html += renderRepContact(rep);
        });

        html += `
            </ul>
          </div>
        `;
      });

      html += `</div>`;
    });

    return html;
  }

  /**
   * Render a single rep contact
   */
  function renderRepContact(rep) {
    const name = escapeHtml(rep.name || '');
    const title = rep.title ? `<span class="rep-finder__title">${escapeHtml(rep.title)}</span>` : '';
    const coverageArea = rep.coverage_area
      ? `<span class="rep-finder__coverage-area">${escapeHtml(rep.coverage_area)}</span>`
      : '';
    const phone = rep.phone
      ? `<a href="tel:${rep.phone.replace(/\D/g, '')}" class="rep-finder__phone">${escapeHtml(rep.phone)}</a>`
      : '';
    const email = rep.email
      ? `<a href="mailto:${rep.email}" class="rep-finder__email">${escapeHtml(rep.email)}</a>`
      : '';
    // rep.website may arrive as a string OR a {url, text} link object
    // OR a {value} field object (Shopify URL metafield drop variants).
    // Coerce to a clean URL string regardless of shape.
    const websiteUrl = typeof rep.website === 'object' && rep.website
      ? (rep.website.url || rep.website.value || '')
      : (rep.website || '');
    const website = websiteUrl
      ? `<a href="${escapeHtml(websiteUrl)}" target="_blank" rel="noopener" class="rep-finder__website">Website</a>`
      : '';

    return `
      <li class="rep-finder__contact">
        <span class="rep-finder__name">${name}</span>
        ${title}
        ${coverageArea}
        <div class="rep-finder__contact-links">
          ${phone}
          ${email}
          ${website}
        </div>
      </li>
    `;
  }

  function clearResults() {
    if ($regionTitle) $regionTitle.textContent = '';
    if ($list) $list.innerHTML = '';
    if ($empty) $empty.style.display = 'none';
    if ($placeholder) $placeholder.style.display = 'block';
    if ($dropdown) $dropdown.value = '';

    // Reset map highlighting when clearing results
    STATE.previousRegion = STATE.activeRegion;
    STATE.activeRegion = null;
  }

  // ─────────────────────────────────────────
  // URL Deep Linking
  // ─────────────────────────────────────────
  function updateURL() {
    const url = new URL(window.location);

    // Only add zone param if not default (usa)
    if (STATE.activeZone !== 'usa') {
      url.searchParams.set('zone', STATE.activeZone);
    } else {
      url.searchParams.delete('zone');
    }

    // Add region param if selected
    if (STATE.activeRegion) {
      url.searchParams.set('region', STATE.activeRegion);
    } else {
      url.searchParams.delete('region');
    }

    history.replaceState(null, '', url);
  }

  function loadFromURL() {
    const params = new URLSearchParams(window.location.search);
    const zone = params.get('zone') || 'usa';
    const region = params.get('region');

    // Switch to zone
    switchZone(zone);

    // Select region if provided (delay to ensure map is loaded)
    if (region) {
      setTimeout(() => {
        handleRegionClick(region);

        // Also highlight the state in SimpleMaps if possible
        const config = ZONE_CONFIG[zone];
        if (config.mapObj) {
          const mapObj = config.mapObj();
          if (mapObj && typeof mapObj.state_zoom === 'function') {
            // Optionally zoom to state
            // mapObj.state_zoom(region);
          }
        }
      }, 300);
    }
  }

  // ─────────────────────────────────────────
  // SimpleMaps Hooks
  // ─────────────────────────────────────────

  // Helper: Set state colors and labels using SimpleMaps API (state_specific)
  function setStateStyle(mapObj, stateId, options) {
    if (!mapObj || !mapObj.mapdata || !mapObj.mapdata.state_specific) return;

    if (!mapObj.mapdata.state_specific[stateId]) {
      mapObj.mapdata.state_specific[stateId] = {};
    }

    const stateSpec = mapObj.mapdata.state_specific[stateId];

    if (options.reset) {
      // Reset to defaults by deleting overrides
      delete stateSpec.color;
      delete stateSpec.hover_color;
      delete stateSpec.label_color;
      delete stateSpec.label_hover_color;
    } else {
      // Set custom colors
      if (options.color !== undefined) stateSpec.color = options.color;
      if (options.hoverColor !== undefined) stateSpec.hover_color = options.hoverColor;
      if (options.labelColor !== undefined) stateSpec.label_color = options.labelColor;
      if (options.labelHoverColor !== undefined) stateSpec.label_hover_color = options.labelHoverColor;
    }
  }

  // Helper: Apply label colors using SimpleMaps internal state/label objects
  // SimpleMaps stores Raphael label elements in mapObj.states[id].sm.labels
  function applyLabelColors(mapObj, stateId, fillColor, hoverColor) {
    if (!mapObj || !mapObj.states || !mapObj.states[stateId]) return;

    const stateElement = mapObj.states[stateId];
    if (!stateElement || !stateElement.sm || !stateElement.sm.labels) return;

    stateElement.sm.labels.forEach(function (label) {
      if (label && label.sm) {
        // Update SimpleMaps internal attribute cache
        label.sm.attributes.fill = fillColor;
        label.sm.over_attributes.fill = hoverColor;
        label.sm.out_attributes.fill = fillColor;
        // Apply immediately using Raphael's attr method
        label.attr({ fill: fillColor });
      }
    });
  }

  // Helper: Apply active state styling (called from hooks, no refresh needed)
  function applyActiveStateStyling(mapObj) {
    if (!STATE.activeRegion || !mapObj) return;

    // Set state_specific for state colors
    setStateStyle(mapObj, STATE.activeRegion, {
      color: MAP_COLORS.selected,
      hoverColor: MAP_COLORS.selected,
      labelColor: MAP_COLORS.white,
      labelHoverColor: MAP_COLORS.white
    });

    // Apply label colors directly via SimpleMaps internal label objects
    applyLabelColors(mapObj, STATE.activeRegion, MAP_COLORS.white, MAP_COLORS.white);
  }

  // Helper: Reset a state to default styling
  function resetStateToDefault(mapObj, stateId) {
    if (!mapObj || !stateId) return;

    setStateStyle(mapObj, stateId, { reset: true });

    // Reset label colors to defaults
    applyLabelColors(mapObj, stateId, MAP_COLORS.label, MAP_COLORS.labelHover);

    // Call refresh to apply the state color reset
    if (typeof mapObj.refresh === 'function') {
      mapObj.refresh();
    }
  }

  function setupMapHooks() {
    // USA map hooks
    if (window.simplemaps_usmap) {
      // Called when a state is clicked
      window.simplemaps_usmap.hooks.click_state = function (id) {
        handleRegionClick(id);
      };

      // Called after map finishes loading
      window.simplemaps_usmap.hooks.complete = function () {
        if (STATE.activeZone === 'usa' && STATE.activeRegion) {
          applyActiveStateStyling(window.simplemaps_usmap);
        }
      };

      // Called after refresh completes - re-apply active state styling
      window.simplemaps_usmap.hooks.refresh_complete = function () {
        if (STATE.activeZone === 'usa' && STATE.activeRegion) {
          applyActiveStateStyling(window.simplemaps_usmap);
        }
      };
    }

    // Canada map hooks
    if (window.simplemaps_canadamap) {
      // Called when a state/province is clicked
      window.simplemaps_canadamap.hooks.click_state = function (id) {
        handleRegionClick(id);
      };

      // Called after map finishes loading
      window.simplemaps_canadamap.hooks.complete = function () {
        if (STATE.activeZone === 'canada' && STATE.activeRegion) {
          applyActiveStateStyling(window.simplemaps_canadamap);
        }
      };

      // Called after refresh completes - re-apply active state styling
      window.simplemaps_canadamap.hooks.refresh_complete = function () {
        if (STATE.activeZone === 'canada' && STATE.activeRegion) {
          applyActiveStateStyling(window.simplemaps_canadamap);
        }
      };
    }
  }

  // ─────────────────────────────────────────
  // Highlight Selected Region on Map
  // ─────────────────────────────────────────
  function highlightSelectedRegion(regionId) {
    const config = ZONE_CONFIG[STATE.activeZone];
    if (!config || config.type !== 'map') return;

    const mapObj = config.mapObj ? config.mapObj() : null;
    if (!mapObj) return;

    // Reset previous state to defaults (just update state_specific, no refresh yet)
    if (STATE.previousRegion && STATE.previousRegion !== regionId) {
      setStateStyle(mapObj, STATE.previousRegion, { reset: true });
    }

    // Set active state styling in state_specific
    setStateStyle(mapObj, regionId, {
      color: MAP_COLORS.selected,
      hoverColor: MAP_COLORS.selected,
      labelColor: MAP_COLORS.white,
      labelHoverColor: MAP_COLORS.white
    });

    // Refresh to apply state color changes
    if (typeof mapObj.refresh === 'function') {
      mapObj.refresh();
    }

    // Apply label colors directly after refresh (refresh doesn't update labels from state_specific)
    // Reset previous state's label to default
    if (STATE.previousRegion && STATE.previousRegion !== regionId) {
      applyLabelColors(mapObj, STATE.previousRegion, MAP_COLORS.label, MAP_COLORS.labelHover);
    }
    // Set active state's label to white
    applyLabelColors(mapObj, regionId, MAP_COLORS.white, MAP_COLORS.white);
  }

  // ─────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function pluralize(type) {
    // Simple pluralization for display headings
    if (type.endsWith('Manager')) return type + 's';
    if (type.endsWith('Rep')) return type + 's';
    if (type.endsWith('Account')) return type + 's';
    if (type.endsWith('Dealer')) return type + 's';
    if (type.endsWith('room')) return type + 's';
    return type;
  }

  // ─────────────────────────────────────────
  // Data Validation (Console Warnings)
  // ─────────────────────────────────────────
  function validateRepData() {
    const repData = window.repData || [];
    if (repData.length === 0) return;

    // Valid region codes for each zone
    const validUSA = new Set([
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
    ]);

    const validCanada = new Set([
      'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
    ]);

    // For International, use keys from COUNTRY_NAMES (comprehensive list)
    const validInternational = new Set(Object.keys(COUNTRY_NAMES));

    const errors = [];

    repData.forEach((rep, index) => {
      const repIdentifier = rep.name || `Rep at index ${index}`;
      const zone = rep.zone;
      const regions = Array.isArray(rep.regions) ? rep.regions : [];

      if (regions.length === 0) {
        errors.push(`⚠️ ${repIdentifier}: No regions specified`);
        return;
      }

      regions.forEach(regionCode => {
        let isValid = false;
        let expectedFormat = '';

        if (zone === 'USA') {
          isValid = validUSA.has(regionCode);
          expectedFormat = 'US state code (e.g., TX, CA, NY)';
        } else if (zone === 'Canada') {
          isValid = validCanada.has(regionCode);
          expectedFormat = 'Canadian province code (e.g., ON, BC, QC)';
        } else if (zone === 'Other') {
          isValid = validInternational.has(regionCode);
          expectedFormat = 'ISO 3166-1 alpha-2 country code (e.g., GB, DE, JP)';
        } else {
          errors.push(`⚠️ ${repIdentifier}: Invalid zone "${zone}" (expected USA, Canada, or Other)`);
          return;
        }

        if (!isValid) {
          errors.push(`⚠️ ${repIdentifier} (${zone}): Invalid region code "${regionCode}" — expected ${expectedFormat}`);
        }
      });
    });

    if (errors.length > 0) {
      console.group('%c🔍 Rep Finder: Data Validation Warnings', 'color: #ff9800; font-weight: bold;');
      console.log('The following rep entries have invalid region codes:');
      errors.forEach(err => console.warn(err));
      console.log('These reps may not appear when users click on map regions.');
      console.groupEnd();
    } else {
      console.log('%c✅ Rep Finder: All rep data validated successfully', 'color: #4caf50;');
    }
  }

  // ─────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────
  function init() {
    // Cache DOM references
    cacheDOMRefs();

    // Verify required elements exist
    if (!$tabs.length || !$list) {
      console.warn('Rep Finder: Required DOM elements not found');
      return;
    }

    // Setup tab click handlers
    $tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        switchZone(tab.dataset.zone);
      });
    });

    // Setup dropdown change handler
    if ($dropdown) {
      $dropdown.addEventListener('change', (e) => {
        const regionId = e.target.value;
        if (regionId) {
          handleRegionClick(regionId);
        }
      });
    }

    // Validate rep data and log any errors to console
    validateRepData();

    // Setup SimpleMaps click hooks
    setupMapHooks();

    // Load initial state from URL or defaults
    loadFromURL();

    console.log('Rep Finder initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
